import React, {useEffect} from 'react';
import {MapContainer, Marker, Polyline, Popup, TileLayer} from 'react-leaflet';
import L from "leaflet";
import 'leaflet/dist/leaflet.css';
import Constants from "../../../utils/constants";
import {divIcon} from 'leaflet';
import CustomTileLayer from "../layout/map/CustomTileLayer.js";
import FlyToLaveuse from "./FlyToLaveuse.js";
import FitRouteToLaveuse from "./FitRouteToLaveuse.js";
import FitTourneeToLaveuse from "./FitTourneeToLaveuse.js";
import markerVert from '../../../assets/images/marker-vert.png';
import markerGris from '../../../assets/images/marker-gris.png';
import markerOrange from '../../../assets/images/marker-orange.png';
import markerRouge from '../../../assets/images/marker-rouge.png';

const STATUS_MARKER = {
    fait: markerVert,
    impossible: markerRouge,
    maintenance: markerOrange,
    todo: markerGris,
};

const STATUS_COLORS = {
    fait: '#4CAF50',
    impossible: '#F44336',
    maintenance: '#FF9800',
    todo: '#9E9E9E',
};

const STATUS_LABELS = {
    fait: '✓ Fait',
    impossible: '✗ Impossible',
    maintenance: '⚠ Maintenance',
    todo: '○ À faire',
};

function LaveuseMap({laveuses, telematics, selectedLaveuse, onSelect, showRoute, routeFitKey, showTournee, tourneeFitKey, context, flyKey, children, ...props}) {
    const [center, setCenter] = React.useState([46.603354, 1.888334]);
    const mapRef = React.useRef(null);

    useEffect(() => {
        if (mapRef.current) {
            mapRef.current.setView(center, 8);
        }
    }, [center]);

        function getIcon(laveuse, engineState) {
            const badgeHtml = `
            <div style="
                position: absolute;
                top: -22px;
                left: 50%;
                transform: translateX(-50%);
                background: rgba(0,0,0,0.45);
                color: white;
                font-size: 14px;
                font-weight: 500;
                padding: 2px 8px;
                border-radius: 20px;
                white-space: nowrap;
                font-family: sans-serif;
            ">${laveuse?.description}</div>
        `

            return divIcon({
                className: 'my-div-icon',
                html: `
                <div style="position: relative; display: inline-block;">
                    ${badgeHtml}
                    <img class="marker-image" src="/images/mini_laveuse.webp"/>
                </div>
            `,
                iconSize: [(2284 / 15), (918 / 15)],
                iconAnchor: [(2284 / 15) / 2, (918 / 15)],
            });
        }

    // Route points pour la laveuse sélectionnée
    const routePoints = (showRoute && selectedLaveuse)
        ? (telematics?.[selectedLaveuse.id]?.route ?? []).map(p => [p.latitude, p.longitude])
        : [];

    // Conteneurs de la tournée du jour
    const tourneeConteneurs = (showTournee && context?.context?.tournees)
        ? context.context.tournees.flatMap(t => t.conteneurs)
        : [];

    const tourneeBoundsPoints = tourneeConteneurs
        .filter(c => c.latitude && c.longitude)
        .map(c => [parseFloat(c.latitude), parseFloat(c.longitude)]);

    return (
        <div style={{position: 'relative', height: '85vh', width: '100%'}}>
            <MapContainer
                ref={mapRef}
                center={center}
                zoom={6}
                style={{height: "100%", width: "100%"}}>
                <CustomTileLayer maxZoom={19}/>

                {/* Si route affichée, on fit les bounds sur la route au lieu de flyTo */}
                {showRoute && routePoints.length > 0
                    ? <FitRouteToLaveuse points={routePoints} trigger={routeFitKey}/>
                    : <FlyToLaveuse selected={selectedLaveuse} telematics={telematics} flyKey={flyKey}/>
                }

                {/* Fit sur la bbox de la tournée au clic sur le toggle */}
                <FitTourneeToLaveuse points={tourneeBoundsPoints} trigger={tourneeFitKey}/>

                {laveuses && laveuses.map((laveuse, i) => {
                    const t = telematics?.[laveuse.id];
                    const live = t?.live ?? laveuse.live;
                    const lat = live?.latitude;
                    const lng = live?.longitude;
                    if (lat == null || lng == null) return null;

                    return (
                        <Marker
                            key={`${i}-${laveuse.id}-${lat}-${lng}`}
                            position={[lat, lng]}
                            icon={getIcon(laveuse, t?.engineState)}
                            eventHandlers={{
                                click: () => onSelect?.(laveuse),
                            }}
                        />
                    );
                })}

                {/* Route du jour */}
                {routePoints.length > 0 && (
                    <Polyline positions={routePoints} pathOptions={{weight: 4, color: '#007bff'}}/>
                )}

                {/* Pins de la tournée */}
                {tourneeConteneurs.map((c) => {
                    if (!c.latitude || !c.longitude) return null;
                    const color = STATUS_COLORS[c.status] ?? '#9E9E9E';
                    const imgSrc = STATUS_MARKER[c.status] ?? markerGris;
                    const icon = divIcon({
                        className: 'my-div-icon',
                        html: `<img class="marker-image" src="${imgSrc}"/>`,
                        iconSize: [56 / 1.5, 76 / 1.5],
                        iconAnchor: [56 / 3, 76 / 1.5],
                    });
                    return (
                        <Marker
                            key={`conteneur-${c.id}`}
                            position={[parseFloat(c.latitude), parseFloat(c.longitude)]}
                            icon={icon}
                        >
                            <Popup>
                                <div style={{minWidth: 180, fontSize: 13}}>
                                    <strong>{c.adresse}</strong><br/>
                                    <span style={{color: '#718096'}}>{c.nomenclature}{c.quantite > 1 ? ` × ${c.quantite}` : ''}</span><br/>
                                    <span style={{color, fontWeight: 600}}>{STATUS_LABELS[c.status]}</span>
                                    {c.nombreLaves != null && <><br/>Lavés : {c.nombreLaves}</>}
                                    {c.dateIntervention && <><br/>🕐 {c.dateIntervention.split(' ')[1]}</>}
                                    {c.problemes?.length > 0 && <><br/><span style={{color: '#FF9800'}}>⚠ {c.problemes.join(', ')}</span></>}
                                    {c.observation && <><br/><em style={{color: '#718096'}}>{c.observation}</em></>}
                                </div>
                            </Popup>
                        </Marker>
                    );
                })}
            </MapContainer>
            {/* Overlay children (detail panel) */}
            {children}
        </div>
    );
}

export default LaveuseMap;
